'use client';

import { useCallback, useState } from 'react';

import { useUpdateEffect } from './useUpdateEffect';

export function useLocalStorage<Value>(
  key: string,
  initialValue: Value,
  storageType: 'local' | 'session' = 'local',
) {
  const storage = storageType === 'local' ? localStorage : sessionStorage;

  const getInitialValue = useCallback(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    const storedValue = storage.getItem(key);

    if (storedValue) {
      try {
        return JSON.parse(storedValue) as Value;
      } catch {
        return initialValue;
      }
    }

    return initialValue;
  }, [initialValue, key, storage]);

  const [value, setValue] = useState(getInitialValue);

  useUpdateEffect(() => {
    setValue(getInitialValue());
  }, [getInitialValue]);

  useUpdateEffect(() => {
    storage.setItem(key, JSON.stringify(value));
  }, [value, key, storage]);

  return { setValue, value };
}
